import React, { FormEvent, useEffect, useId, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Checkbox,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { StyledDefaultPrimaryButton } from 'styles';
import SearchableTenantSelect from 'TeamModule/components/Filters/SearchableTenantSelect';
import SearchableRealmSelect from 'TeamModule/components/Filters/SearchableRealmSelect';
import { SandboxIsoStateIds } from 'SandboxModule/hooks/useSandboxes';
import {
  useMigrationPreflight,
  useStartRealmMigration,
} from 'RealmModule/hooks/useStartRealmMigration';
import { PreflightCheckResult } from 'RealmModule/services/realmMigrationApi';
import { TeamSearchItem } from 'TeamModule/api/searchTeamsByFieldsApi';
import {
  findTeamWindow,
  useClearScheduledMaintenance,
  useScheduledMaintenance,
  useSetScheduledMaintenance,
} from 'RealmModule/hooks/useScheduledMaintenance';
import { extractApiErrorMessage } from 'utils/extractApiErrorMessage';
import { useNotification } from 'hooks/useNotification';
import SourceTeamSelect from './SourceTeamSelect';
import TeamIntegrationsSummary from './TeamIntegrationsSummary';
import MaintenanceWindowFields from './MaintenanceWindowFields';
import { toEpochSeconds, toLocalInputValue } from '../windowTime';

/**
 * The documented upper bound on a migration's runtime. A window shorter than this can
 * close while the data phase is still going.
 */
const MIN_WINDOW_MS = 90 * 60 * 1000;

const RESULT_COLOR: Record<PreflightCheckResult, 'success' | 'error' | 'warning' | 'default'> = {
  pass: 'success',
  fail: 'error',
  warn: 'warning',
  unknown: 'default',
};

/**
 * The worst of several check results.
 *
 * A service row carries three independent results and any one of them failing is enough to
 * fail the whole pre-flight. Chipping only `registeredOnTarget` would put a green chip on a
 * service whose credentials are broken, under a footer telling the operator to go resolve a
 * failure that nothing on screen is marked as.
 */
const worstResult = (...results: PreflightCheckResult[]): PreflightCheckResult =>
  results.find((r) => r === 'fail') ??
  results.find((r) => r === 'warn') ??
  results.find((r) => r === 'unknown') ??
  'pass';

/**
 * Start a realm migration.
 *
 * Deliberately a two-step flow rather than a submit button. A migration blocks the team's
 * traffic for 15-90 minutes and runs `DROP SCHEMA PUBLIC CASCADE` on the target realm's
 * rails-api and api-server databases, so the operator sees the computed plan and the
 * named destructive actions BEFORE anything can be started, and re-confirms by typing the
 * team id. The pre-flight is read-only, so re-running it costs nothing.
 */
export const StartRealmMigrationForm = ({
  sourceTenant,
  onSourceTenantChange,
}: {
  /**
   * Owned by the page, because the monitoring cards have to authenticate as this tenant
   * too — a migration started out of another tenant is invisible to a list read as the
   * one the admin happens to be viewing.
   */
  sourceTenant: string;
  onSourceTenantChange: (tenant: string) => void;
}) => {
  const helperId = useId();
  const [sourceRealmId, setSourceRealmId] = useState('');
  const [targetTenant, setTargetTenant] = useState('');
  const [targetRealmId, setTargetRealmId] = useState('');
  const [team, setTeam] = useState<TeamSearchItem | null>(null);
  const [migrateArchivedIntegrations, setMigrateArchived] = useState(false);
  const [confirmation, setConfirmation] = useState('');

  // Default to a window starting now and long enough for a typical run. A migration takes
  // 15-90 minutes, so four hours is headroom rather than a guess at the duration.
  const [windowEnabled, setWindowEnabled] = useState(true);
  // Only the end is a field. The window opens when Start is pressed, so its start is
  // stamped then rather than carried in state where an idle form would let it go stale.
  const [windowEnd, setWindowEnd] = useState(() =>
    toLocalInputValue(new Date(Date.now() + 4 * 60 * 60 * 1000)),
  );

  const teamId = team?.teamId ?? '';

  // A realm id belongs to exactly one tenant, and SearchableSelect does not clear a
  // controlled value that has dropped out of its options — it just renders blank while
  // still holding it. Without these the field looks empty but the old id is still what
  // gets submitted, so the operator pre-flights a pair they cannot see. Same idiom as
  // BakIntegrationProvisionModal and the Activity Log filters.
  const changeSourceTenant = (tenant: string) => {
    onSourceTenantChange(tenant);
    setSourceRealmId('');
  };

  const changeTargetTenant = (tenant: string) => {
    setTargetTenant(tenant);
    setTargetRealmId('');
  };

  const { showError, showWarning } = useNotification();
  const { runPreflight, report, isRunning, reset } = useMigrationPreflight();
  const { setWindow, isSettingWindow } = useSetScheduledMaintenance();
  const { clearWindow, isClearingWindow } = useClearScheduledMaintenance();

  /**
   * Set when a start failed with an unknown outcome (5xx or transport).
   *
   * Blocks further attempts. The migration may be running, and a retry would schedule a
   * second window over the first — then take the 409 it earns, treat that as a definite
   * refusal, and roll back using a snapshot taken when there was nothing there. The
   * result is maintenance stripped from a live migration by the cleanup of the attempt
   * that followed it.
   */
  const [startOutcomeUnknown, setStartOutcomeUnknown] = useState(false);

  // Only worth reading once a team is in play — it is a GrowthBook admin call, and its
  // whole purpose here is to show this team's existing window and the two flag-level
  // states that would make scheduling fail.
  const {
    state: maintenanceState,
    isLoading: maintenanceLoading,
    isError: maintenanceError,
  } = useScheduledMaintenance({ enabled: !!teamId });
  const existingWindow = findTeamWindow(maintenanceState, {
    tenant: sourceTenant,
    realmId: sourceRealmId,
    teamId,
  });
  // Any non-force rule anywhere on the flag makes galaxy refuse to write it at all, even
  // one scoped to another team — so this is deliberately not filtered to this team's rule.
  const unwritableRule = maintenanceState?.windows.find((w) => w.type !== 'force');
  /**
   * The coordinates the window was actually scheduled against.
   *
   * Captured at schedule time rather than read from state on the way back: the rollback
   * has to address the rule that was written, and the form may have moved on by then.
   */
  const scheduledFor = useRef<{
    tenant: string;
    realmId: string;
    teamId: string;
    /**
     * The window that was there before, when scheduling REPLACED one.
     *
     * Rolling back by clearing would delete a window the operator never asked us to
     * touch, and could drop a team out of an active maintenance state it was already in.
     * Null means there was nothing to restore and clearing is the correct undo.
     */
    previous: { startTime: number; endTime: number } | 'unknown' | null;
  } | null>(null);

  const { startMigration, isStarting } = useStartRealmMigration({
    onSuccess: () => {
      // The migration is running and owns the window from here.
      scheduledFor.current = null;
      reset();
      setConfirmation('');
    },
    // Galaxy answered and said nothing started, so the window it was set for is now a team
    // sitting in maintenance for a migration that will never run. Rolled back only on this
    // definite refusal — a transport failure is ambiguous and the migration may well be
    // under way, where clearing the window would be the destructive mistake.
    onIndeterminate: () => {
      setStartOutcomeUnknown(true);
      const scheduled = scheduledFor.current;
      // Left in place on purpose — the migration may be running and would need it.
      showWarning(
        scheduled
          ? `It is not known whether the migration started, so team ${scheduled.teamId}'s maintenance window has been left in place. Check Recent Migrations before trying again — if no run appears, clear the window in GrowthBook.`
          : 'It is not known whether the migration started. Check Recent Migrations before trying again.',
        { persist: true },
      );
    },
    onRefused: () => {
      const scheduled = scheduledFor.current;
      scheduledFor.current = null;
      if (!scheduled) return;

      const teams = [scheduled.teamId];
      const { previous } = scheduled;

      // Nothing here is safe to undo blind: clearing would strip maintenance the team may
      // already have been in, and there is no record of what to put back.
      if (previous === 'unknown') {
        showWarning(
          `The migration did not start, but team ${scheduled.teamId}'s maintenance window was left in place because this page could not tell what it replaced. Check it in GrowthBook.`,
          { persist: true },
        );
        return;
      }

      const undo = previous
        ? // Put back exactly what was there, rather than removing a window the operator
          // never asked us to change.
          setWindow({
            tenant: scheduled.tenant,
            realmId: scheduled.realmId,
            teamIds: teams,
            startTime: previous.startTime,
            endTime: previous.endTime,
          })
        : clearWindow({ tenant: scheduled.tenant, realmId: scheduled.realmId, teamIds: teams });

      undo.catch(() =>
        showWarning(
          previous
            ? `The migration did not start, and team ${scheduled.teamId}'s previous maintenance window could not be restored. It now holds the window scheduled for this migration — fix it in GrowthBook.`
            : `The migration did not start, and the maintenance window for team ${scheduled.teamId} could not be removed. Clear it in GrowthBook.`,
          { persist: true },
        ),
      );
    },
  });

  // Scheduling and starting are one operation from the operator's point of view, so they
  // are one in-flight state. Freezing only the Start button would leave the coordinates
  // editable across the await, and this handler would then start a migration with the
  // coordinates it captured while the form and the monitoring cards had moved on.
  // The rollback is part of the operation too: while a clear is in flight the form must
  // not accept a retry, or the new window can be scheduled and then removed by the
  // previous attempt's clear landing late.
  const isBusy = isStarting || isSettingWindow || isClearingWindow;

  const coordinates = {
    teamIds: teamId.trim(),
    sourceRealmId,
    targetRealmId,
    sourceTenant,
    targetTenant,
    migrateArchivedIntegrations,
  };

  const hasAllInputs = Object.values({
    teamId: teamId.trim(),
    sourceTenant,
    sourceRealmId,
    targetTenant,
    targetRealmId,
  }).every(Boolean);

  // The team was chosen from a search scoped to one realm, so it is meaningless — and
  // potentially a different team entirely — once the source moves. Keyed on the source
  // only; including teamId would clear the selection on the render that made it.
  useEffect(() => {
    setTeam(null);
  }, [sourceTenant, sourceRealmId]);

  // Any input change invalidates the plan the operator was shown. Keeping a stale report
  // on screen would let them confirm a migration against a plan that no longer describes
  // what would run.
  useEffect(() => {
    reset();
    setConfirmation('');
  }, [
    sourceTenant,
    sourceRealmId,
    targetTenant,
    targetRealmId,
    teamId,
    migrateArchivedIntegrations,
    reset,
  ]);

  const onPreflight = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasAllInputs) return;
    // Ask for sizing here: this is the deliberate pre-submit check, not a keystroke.
    runPreflight({ ...coordinates, includeSizeEstimate: true });
  };

  const endSeconds = toEpochSeconds(windowEnd);
  // Only the time-independent half is checked during render. Whether the end is still in
  // the future depends on the current time, so computing it here would produce a button
  // whose disabled state is stale until the next unrelated re-render; `onStart` checks it
  // at the one moment the answer is true.
  //
  // Scheduling is also blocked until the existing-window read lands: until then there is
  // no way to know what a write would replace, and treating "not read yet" as "nothing
  // there" is what makes a rollback delete a window it never owned.
  // A failed read is not a soft state to work around: without it there is no way to know
  // what a write would replace, and a later rollback could only leave the replacement in
  // place — permanently losing whatever rule was there. Scheduling waits for a real answer.
  const windowValid =
    !windowEnabled || (endSeconds !== null && !maintenanceLoading && !maintenanceError);

  const canStart =
    !!report?.ok &&
    confirmation.trim() === teamId.trim() &&
    windowValid &&
    !isBusy &&
    !startOutcomeUnknown;

  /**
   * Schedule the window, then start.
   *
   * Sequenced rather than fired together, and the migration is abandoned if the window
   * fails: galaxy refuses a window for reasons an operator needs to act on — the flag
   * disabled for this environment, a non-force rule making it unwritable, or a later
   * window that would pull the team out of maintenance now. Starting anyway would move
   * the team's data with the product still serving it normally.
   *
   * The window is deliberately NOT cleared here. The migration outlives this page, and
   * once the team re-indexes into the target realm galaxy can no longer address the rule
   * to clear it.
   */
  const onStart = async () => {
    // A previous attempt left the outcome unknown; retrying could strip maintenance from
    // a migration that is actually running.
    if (startOutcomeUnknown) return;

    // Belt and braces alongside that guard: from here, this attempt owns the rollback. A
    // ref left over from a previous attempt would roll back ITS window using ITS
    // coordinates — against a migration that may still be moving data.
    scheduledFor.current = null;

    // Snapshotted before the write, because the read this came from is about to be stale.
    const replaced = existingWindow;
    /*
     * Whether the rule about to be replaced could be put back exactly as it was.
     *
     * `set` can only express a start/end pair, so anything the prior rule carried beyond
     * that is lost on a restore — and a restore producing a semantically different rule is
     * worse than none, because it looks like it worked. Four ways it is not restorable:
     *
     *  - the read failed, so there is no snapshot at all;
     *  - its value did not parse into a start/end pair;
     *  - it carries GrowthBook schedule gates, which this API cannot write;
     *  - its window has already elapsed, and `set` requires a future end — the restore
     *    would be refused, leaving the replacement live.
     */
    const unreadablePrior =
      maintenanceError ||
      (!!replaced &&
        (replaced.startTime === null ||
          replaced.endTime === null ||
          replaced.hasScheduleRules ||
          replaced.endTime * 1000 <= Date.now()));

    if (windowEnabled) {
      const now = Date.now();
      if ((endSeconds as number) * 1000 <= now) {
        showError('The maintenance window has already ended. Set an end time in the future.');
        return;
      }
      // A default computed at mount goes stale while the form sits open: after a few hours
      // idle, "now + 4h" is a ten-minute window. A migration runs for up to 90 minutes, so
      // a window closing inside that drops the traffic block partway through the data
      // phase — the one outcome this feature exists to prevent.
      if ((endSeconds as number) * 1000 - now < MIN_WINDOW_MS) {
        showError(
          'The window would close before the migration could finish. A migration takes up to 90 minutes — push the end time out.',
        );
        return;
      }
      try {
        const result = await setWindow({
          tenant: sourceTenant,
          realmId: sourceRealmId,
          teamIds: [teamId],
          // Stamped here, not held in state: the window opens with the migration, and a
          // value captured at mount would quietly shrink the operator's headroom on a
          // form that had been sitting open.
          startTime: Math.floor(now / 1000),
          endTime: endSeconds as number,
        });
        const applied = result?.data?.applied?.[0];
        // The read is cached, so another operator — or a hand edit in GrowthBook — can
        // create a rule between it and this write. GrowthBook reporting an UPDATE when
        // the snapshot said there was nothing to update proves exactly that, and the
        // snapshot is then describing a world that no longer exists.
        const snapshotContradicted = applied?.action === 'updated' && !replaced;

        scheduledFor.current = {
          tenant: sourceTenant,
          realmId: sourceRealmId,
          teamId,
          // A prior state this page cannot faithfully reproduce must never become a
          // clear, and must never become a lossy "restore" either.
          previous:
            unreadablePrior || snapshotContradicted
              ? 'unknown'
              : replaced
                ? { startTime: replaced.startTime as number, endTime: replaced.endTime as number }
                : null,
        };
        if (unreadablePrior || snapshotContradicted) {
          showWarning(
            `Team ${teamId} may have had a maintenance window this page could not read, and it has now been replaced. If the migration does not start, check it in GrowthBook.`,
            { persist: true },
          );
        }
      } catch (error) {
        showError(
          `Maintenance window not scheduled, so the migration was not started: ${extractApiErrorMessage(
            error as never,
          )}`,
        );
        return;
      }
    }
    startMigration(coordinates);
  };

  return (
    <form onSubmit={onPreflight}>
      <h3>Start a Realm Migration</h3>

      {/*
        Frozen while the start request is in flight. The request carries the coordinates it
        was submitted with, but the page's monitoring follows `sourceTenant` live — so
        editing it mid-flight would point the history and steps cards at a tenant that
        cannot contain the run the success toast is about to tell the operator to watch.

        The fieldset covers the native controls — the checkbox, the team Autocomplete's
        input, the submit button. It does NOT cover the four selects: MUI renders those as
        `div[role=combobox]`, which is not a listed form control, so a disabled fieldset
        leaves them clickable. They each need the prop, which is why it is passed below.
      */}
      <Box
        component="fieldset"
        disabled={isBusy}
        sx={{
          gap: 12,
          display: 'flex',
          flexDirection: 'column',
          border: 0,
          p: 0,
          m: 0,
          minInlineSize: 'auto',
        }}
      >
        <Typography variant="subtitle2">Source</Typography>
        <SearchableTenantSelect
          value={sourceTenant}
          onChange={changeSourceTenant}
          label="Source Tenant"
          labelId={`${helperId}-source-tenant`}
          disabled={isBusy}
        />
        <SearchableRealmSelect
          tenantName={sourceTenant}
          value={sourceRealmId}
          onChange={setSourceRealmId}
          label="Source Realm"
          labelId={`${helperId}-source-realm`}
          disabled={isBusy}
          isoId={SandboxIsoStateIds.SandboxTable}
        />
        {/*
          The team belongs to the SOURCE. It is searched within the source realm, the
          maintenance window is scoped to the source realm because that is where the team
          is still indexed, and the data being moved is the source team's. Sitting it
          under Target read as "the team in the target realm", which is not a thing.
        */}
        <SourceTeamSelect
          tenant={sourceTenant}
          realmId={sourceRealmId}
          value={teamId}
          onChange={setTeam}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={migrateArchivedIntegrations}
              onChange={(e) => setMigrateArchived(e.target.checked)}
              slotProps={{
                input: { 'aria-describedby': `${helperId}-archived` },
              }}
            />
          }
          label="Include archived integrations"
        />
        <Typography
          id={`${helperId}-archived`}
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          Widens which agent services are migrated, because the agent set is derived from the
          team&apos;s live integrations rather than from configuration.
        </Typography>

        <TeamIntegrationsSummary
          team={team}
          migrateArchivedIntegrations={migrateArchivedIntegrations}
        />

        <Typography variant="subtitle2">Target</Typography>
        <SearchableTenantSelect
          value={targetTenant}
          onChange={changeTargetTenant}
          label="Target Tenant"
          labelId={`${helperId}-target-tenant`}
          disabled={isBusy}
        />
        <SearchableRealmSelect
          tenantName={targetTenant}
          value={targetRealmId}
          onChange={setTargetRealmId}
          label="Target Realm"
          labelId={`${helperId}-target-realm`}
          disabled={isBusy}
          isoId={SandboxIsoStateIds.SandboxTable}
        />

        <MaintenanceWindowFields
          enabled={windowEnabled}
          onEnabledChange={setWindowEnabled}
          end={windowEnd}
          onEndChange={setWindowEnd}
          existingWindow={existingWindow}
          flagDisabled={maintenanceState ? !maintenanceState.enabled : false}
          unwritableRule={unwritableRule}
          disabled={isBusy}
        />

        <StyledDefaultPrimaryButton type="submit" disabled={!hasAllInputs || isRunning || isBusy}>
          {isRunning ? 'Checking…' : 'Run pre-flight'}
        </StyledDefaultPrimaryButton>
      </Box>

      {isRunning && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 4,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}

      {report && (
        <>
          <Divider sx={{ my: 6 }} />
          <PreflightReportView report={report} />

          <Divider sx={{ my: 6 }} />
          {report.ok ? (
            <Box
              sx={{
                gap: 12,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Alert severity="warning">
                <AlertTitle>This is destructive and cannot be undone</AlertTitle>
                {report.destructive.length > 0 ? (
                  <ul style={{ margin: 0, paddingInlineStart: '1.2em' }}>
                    {report.destructive.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                ) : (
                  'No schema will be dropped by this plan.'
                )}
                <Box
                  sx={{
                    mt: 2,
                  }}
                >
                  The team&apos;s traffic is blocked for the whole run, and the source team is
                  archived when it completes.
                </Box>
              </Alert>

              <TextField
                label={`Type the team id (${teamId.trim()}) to confirm`}
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                fullWidth
              />

              <StyledDefaultPrimaryButton type="button" disabled={!canStart} onClick={onStart}>
                {isSettingWindow
                  ? 'Scheduling window…'
                  : isStarting
                    ? 'Starting…'
                    : 'Start migration'}
              </StyledDefaultPrimaryButton>
            </Box>
          ) : (
            <Alert severity="error">
              <AlertTitle>Pre-flight did not pass</AlertTitle>
              Resolve the failing checks above before starting. Nothing has been changed.
            </Alert>
          )}
        </>
      )}
    </form>
  );
};

/** Renders the pre-flight result: the checklist, the service plan, and the estimate. */
const PreflightReportView = ({
  report,
}: {
  report: NonNullable<ReturnType<typeof useMigrationPreflight>['report']>;
}) => (
  <Box
    sx={{
      gap: 12,
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Typography variant="subtitle2">Pre-flight</Typography>

    <Stack
      sx={{
        gap: 1,
      }}
    >
      {report.checks.map((check) => (
        <Box
          key={check.name}
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          <Chip size="small" label={check.result} color={RESULT_COLOR[check.result]} />
          <Box>
            <Typography variant="body2">{check.name}</Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {check.detail}
            </Typography>
          </Box>
        </Box>
      ))}
    </Stack>

    <Typography variant="subtitle2">Services to migrate</Typography>
    <Stack
      sx={{
        gap: 1,
      }}
    >
      {report.servicesToMigrate.map((service) => {
        const result = worstResult(
          service.registeredOnTarget,
          service.targetCredentials,
          service.sourceCredentials,
        );
        return (
          <Box
            key={service.service}
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start',
            }}
          >
            <Chip size="small" label={result} color={RESULT_COLOR[result]} />
            <Box>
              <Typography variant="body2">
                {service.service}
                {service.sizingTier ? ` — ${service.sizingTier}` : ''}
              </Typography>
              {/* The backend names the exact missing secret key here, so this caption is
                  what makes a credential failure actionable. */}
              {!!service.detail && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {service.detail}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Stack>

    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary',
      }}
    >
      {report.estimate.note}
    </Typography>
  </Box>
);

export default StartRealmMigrationForm;
